import { gsap } from "gsap";
import { clamp, easeOutCubic, hashString, invLerp, seededUnit, smoothstep } from "../utils/math.js";

const EMPHASIS_POS = new Set(["N", "PN", "V", "J", "R", "W"]);
const ENGLISH_WORD_PATTERN = /[A-Za-z]/;
const VOWELISH_CHAR_PATTERN = /[aeiouyAEIOUYぁあかがさざただなはばぱまゃやらわぃいきぎしじちぢにひびぴみりゐぅうくぐすずつづぬふぶぷむゅゆるゔぇえけげせぜてでねへべぺめれゑぉおこごそぞとどのほぼぽもょよろをんァアカガサザタダナハバパマャヤラワィイキギシジチヂニヒビピミリヰゥウクグスズツヅヌフブプムュユルヴェエケゲセゼテデネヘベペメレヱォオコゴソゾトドノホボポモョヨロヲンー]/;

export class LyricRenderer {
  constructor({ currentElement, echoElement, archiveElement, profile }) {
    this.currentElement = currentElement;
    this.echoElement = echoElement;
    this.archiveElement = archiveElement;
    this.profile = profile;
    this.video = null;
    this.activePhrase = null;
    this.previousPhraseText = "";
    this.charViews = [];
    this.wordViews = new Map();
    this.wordSequence = [];
    this.visibleWords = new Set();
    this.isClearing = false;
  }

  setVideo(video) {
    this.video = video;
    this.activePhrase = null;
    this.previousPhraseText = "";
    this.charViews = [];
    this.wordViews.clear();
    this.wordSequence = [];
    this.visibleWords.clear();
    this.isClearing = false;
    this.currentElement.replaceChildren();
    this.echoElement.replaceChildren();
    this.archiveElement.replaceChildren();
  }

  update(snapshot) {
    if (!snapshot.phrase) {
      this.#clearPhrase();
      return;
    }

    if (snapshot.phrase !== this.activePhrase) {
      this.#renderPhrase(snapshot.phrase, snapshot);
    }

    this.#updateWordState(snapshot);

    if (snapshot.entered?.word && snapshot.word) {
      if (this.#isEnglishWord(snapshot.word)) {
        this.#clearFloatingText();
      }

      this.#emitWordEcho(snapshot.word, snapshot);
    }

    this.#updateCharacterState(snapshot);
  }

  #renderPhrase(phrase, snapshot) {
    gsap.killTweensOf(this.currentElement);
    this.isClearing = false;
    const previousText = this.activePhrase?.text || this.previousPhraseText;
    const startsEnglishMoment = this.#phraseHasEnglish(phrase);
    if (startsEnglishMoment) {
      this.#clearFloatingText();
    }

    if (previousText && !startsEnglishMoment) {
      this.#archivePhrase(previousText, snapshot);
    }

    this.activePhrase = phrase;
    this.previousPhraseText = phrase.text;
    this.charViews = [];
    this.wordViews.clear();
    this.wordSequence = [];
    this.visibleWords.clear();
    this.currentElement.replaceChildren();
    this.currentElement.classList.add("has-phrase");
    this.currentElement.dataset.length = String(phrase.charCount || phrase.text.length);
    this.currentElement.style.setProperty("--phrase-font-scale", this.#fontScaleFor(phrase));
    this.currentElement.style.setProperty("--phrase-presence", "0");
    this.currentElement.style.setProperty("--phrase-emotion", this.#phraseEmotion(snapshot).toFixed(3));
    this.currentElement.style.setProperty("--phrase-breath", this.#phraseBreath(phrase).toFixed(3));
    this.currentElement.style.setProperty("--phrase-intimacy", (snapshot.mood?.intimacy ?? 0.65).toFixed(3));
    this.currentElement.style.setProperty("--phrase-release", (snapshot.mood?.release ?? 0).toFixed(3));

    const fragment = document.createDocumentFragment();
    const phraseSeed = hashString(`${phrase.startTime}:${phrase.text}`);

    phrase.children.forEach((word, wordIndex) => {
      const wordElement = document.createElement("span");
      wordElement.className = "lyric-word";
      wordElement.dataset.language = word.language || "unknown";
      wordElement.dataset.emphasis = this.#isEmphasisWord(word) ? "strong" : "normal";
      wordElement.style.setProperty("--word-energy", "0");

      word.children.forEach((char, charIndex) => {
        const charElement = document.createElement("span");
        charElement.className = /\s/.test(char.text) ? "lyric-char lyric-space" : "lyric-char";
        charElement.textContent = /\s/.test(char.text) ? "\u00a0" : char.text;
        charElement.style.setProperty("--char-x", "0px");
        charElement.style.setProperty("--char-y", "18px");
        charElement.style.setProperty("--char-scale", "0.98");
        charElement.style.setProperty("--char-scale-x", "0.98");
        charElement.style.setProperty("--char-scale-y", "0.98");
        charElement.style.setProperty("--char-skew", "0deg");
        charElement.style.setProperty("--char-opacity", "0");
        charElement.style.setProperty("--char-blur", "4px");
        charElement.style.setProperty("--char-glow", "0");
        charElement.style.setProperty("--char-hold", "0");
        charElement.style.setProperty("--char-hold-line", "0");
        wordElement.append(charElement);

        this.charViews.push({
          unit: char,
          element: charElement,
          word,
          holdWeight: this.#holdWeightForChar(char),
          seed: seededUnit(phraseSeed, wordIndex * 31 + charIndex),
        });
      });

      this.wordViews.set(word, wordElement);
      this.wordSequence.push({ unit: word, element: wordElement });
      fragment.append(wordElement);
    });

    this.currentElement.append(fragment);
    gsap.fromTo(
      this.currentElement,
      {
        opacity: 0,
        y: snapshot.section === "chorus" ? 34 : snapshot.section === "verse" ? 12 : 18,
        scale: snapshot.section === "chorus" ? 0.94 : 0.985 + (snapshot.mood?.intimacy ?? 0) * 0.01,
        filter: "blur(10px)",
        "--phrase-presence": 0,
      },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        filter: "blur(0px)",
        "--phrase-presence": 1,
        duration: snapshot.section === "chorus" ? 0.54 : snapshot.section === "verse" ? 0.9 : 0.72,
        ease: snapshot.section === "chorus" ? "expo.out" : "power3.out",
      },
    );
  }

  #updateCharacterState(snapshot) {
    const { position, section, metrics, chorusIntensity } = snapshot;
    const mood = snapshot.mood || {};
    const energy = mood.energy ?? 0.35;
    const intimacy = mood.intimacy ?? 0.65;
    const releaseMood = mood.release ?? 0;
    const leadMs = section === "chorus" ? 260 : section === "pre-chorus" ? 220 : 150 + intimacy * 80;
    const tailMs = section === "chorus" ? 760 : 560 + intimacy * 180;
    const vocal = metrics?.vocal || 0;

    let phraseSustain = 0;

    this.charViews.forEach(({ unit, element, word, holdWeight, seed }) => {
      if (!this.visibleWords.has(word)) return;

      const start = unit.startTime || 0;
      const end = Math.max(unit.endTime || start + 32, start + 32);
      const duration = end - start;
      const reveal = smoothstep(start - leadMs - seed * 90, start + 60, position);
      const release = smoothstep(end, end + tailMs, position);
      const activeWindow = 1 - clamp(Math.abs(position - (start + end) * 0.5) / Math.max(120, end - start));
      const active = unit.contains(position) ? 1 : activeWindow * 0.28;
      const shimmer = Math.sin(position * 0.012 + seed * 18) * 0.5 + 0.5;
      const charProgress = unit.contains(position) ? clamp((position - start) / duration) : 0;
      const holdAttack = smoothstep(0.22, 0.72, charProgress);
      const holdAfterglow = position > end ? (1 - smoothstep(end, end + 280 + duration * 0.22, position)) * 0.34 : 0;
      // A held note is inferred directly from TextAlive character timings:
      // long character duration + a vowel-like glyph produces stretch and a small sustain beam.
      const sustain = holdWeight * Math.max(unit.contains(position) ? holdAttack : 0, holdAfterglow);
      phraseSustain = Math.max(phraseSustain, sustain);

      const emotionalWeight = vocal * 0.65 + energy * 0.5 + releaseMood * 0.42;
      const y = (1 - easeOutCubic(reveal)) * (18 + energy * 12) - release * (9 + releaseMood * 9) + active * (-3 - emotionalWeight * 5);
      const x =
        (seed - 0.5) * (chorusIntensity * 14 + (mood.tension ?? 0) * 5) * (0.35 + shimmer) +
        sustain * (seed - 0.5) * 3;
      const scale = 0.94 + reveal * 0.06 + active * (0.08 + vocal * 0.07 + energy * 0.04) + releaseMood * 0.05;
      const scaleX = scale * (1 + sustain * (0.24 + vocal * 0.16 + chorusIntensity * 0.12));
      const scaleY = scale * (1 + sustain * 0.045);
      const skew = (seed - 0.5) * active * (3 + energy * 6) * (section === "chorus" ? 1 : 0.28) + sustain * 2.2;
      const opacity = clamp(0.06 + reveal * 0.94 - release * 0.52 + active * 0.25);
      const blur = Math.max(0, (1 - reveal) * 5 - active * 2 + release * 1.4);
      const glow = clamp(active * 1.08 + releaseMood * 0.72 + vocal * 0.58 + energy * 0.26 + sustain * 0.55);
      const holdLine = clamp(sustain * (0.82 + vocal * 0.4 + chorusIntensity * 0.36));

      element.style.setProperty("--char-x", `${x.toFixed(2)}px`);
      element.style.setProperty("--char-y", `${y.toFixed(2)}px`);
      element.style.setProperty("--char-scale", scale.toFixed(3));
      element.style.setProperty("--char-scale-x", scaleX.toFixed(3));
      element.style.setProperty("--char-scale-y", scaleY.toFixed(3));
      element.style.setProperty("--char-skew", `${skew.toFixed(2)}deg`);
      element.style.setProperty("--char-opacity", opacity.toFixed(3));
      element.style.setProperty("--char-blur", `${blur.toFixed(2)}px`);
      element.style.setProperty("--char-glow", glow.toFixed(3));
      element.style.setProperty("--char-hold", sustain.toFixed(3));
      element.style.setProperty("--char-hold-line", holdLine.toFixed(3));
      element.classList.toggle("is-held", holdLine > 0.08);
    });

    this.currentElement.style.setProperty("--phrase-sustain", phraseSustain.toFixed(3));
  }

  #updateWordState(snapshot) {
    const vocal = snapshot.metrics?.vocal || 0;
    const energy = snapshot.mood?.energy ?? 0.35;
    const warmth = clamp(((snapshot.metrics?.valence || 0) + 1) * 0.5);
    const arousal = clamp((snapshot.metrics?.arousal || 0) * 0.5 + 0.5);
    const visibleIndexes = this.#visibleWordIndexes(snapshot);
    this.visibleWords = new Set();

    const activeIndex = this.#activeWordIndex(snapshot);

    this.wordSequence.forEach(({ unit: word, element }, index) => {
      const isVisible = visibleIndexes.has(index);
      if (isVisible) {
        this.visibleWords.add(word);
      }

      const progress = isVisible && word.contains(snapshot.position) ? word.progress(snapshot.position) : -1;
      const emphasis = progress >= 0 ? Math.sin(progress * Math.PI) * (0.58 + vocal * 0.42 + energy * 0.42) : 0;
      element.classList.toggle("is-visible", isVisible);
      element.classList.toggle("is-active", progress >= 0);
      element.classList.toggle("is-chorus-focus", snapshot.section === "chorus" && index === activeIndex);
      element.classList.toggle("is-chorus-support", snapshot.section === "chorus" && isVisible && index !== activeIndex);
      element.setAttribute("aria-hidden", isVisible ? "false" : "true");
      element.style.setProperty("--word-energy", Math.min(emphasis, 1.24).toFixed(3));
      element.style.setProperty("--word-vocal", vocal.toFixed(3));
      element.style.setProperty("--word-warmth", warmth.toFixed(3));
      element.style.setProperty("--word-arousal", arousal.toFixed(3));
    });
  }

  #archivePhrase(text, snapshot) {
    if (this.profile.reducedMotion) return;

    const fragment = document.createElement("span");
    fragment.className = "archive-fragment";
    fragment.textContent = this.#archiveExcerpt(text);

    const seed = hashString(`${text}:${snapshot.position}`);
    fragment.style.left = `${12 + seededUnit(seed, 1) * 76}%`;
    fragment.style.top = `${10 + seededUnit(seed, 2) * 72}%`;
    fragment.style.setProperty("--archive-rotate", `${(seededUnit(seed, 3) - 0.5) * 11}deg`);
    fragment.style.setProperty("--archive-scale", `${0.72 + seededUnit(seed, 4) * 0.32}`);

    this.archiveElement.append(fragment);

    gsap.fromTo(
      fragment,
      { opacity: 0, y: 18, filter: "blur(8px)" },
      {
        opacity: snapshot.section === "chorus" ? 0.18 : 0.1,
        y: -20,
        filter: "blur(2px)",
        duration: 2.3,
        ease: "power2.out",
        onComplete: () => {
          gsap.to(fragment, {
            opacity: 0,
            duration: 0.72,
            ease: "sine.inOut",
            onComplete: () => fragment.remove(),
          });
        },
      },
    );

    while (this.archiveElement.childElementCount > 3 && this.archiveElement.firstElementChild) {
      this.archiveElement.firstElementChild.remove();
    }
  }

  #emitWordEcho(word, snapshot) {
    if (this.profile.reducedMotion || !word?.text?.trim()) return;

    const wordElement = this.wordViews.get(word);
    if (!wordElement) return;

    const layerRect = this.echoElement.getBoundingClientRect();
    const wordRect = wordElement.getBoundingClientRect();
    const echo = document.createElement("span");
    echo.className = "word-echo";
    echo.textContent = word.text;
    echo.style.left = `${wordRect.left - layerRect.left + wordRect.width / 2}px`;
    echo.style.top = `${wordRect.top - layerRect.top + wordRect.height / 2}px`;
    echo.style.setProperty("--echo-size", `${Math.max(1, wordRect.height / 48).toFixed(2)}`);

    this.echoElement.append(echo);
    gsap.fromTo(
      echo,
      {
        opacity: snapshot.section === "chorus" ? 0.42 : 0.28,
        scale: 0.9,
        y: 0,
        filter: "blur(0px)",
      },
      {
        opacity: 0,
        scale: snapshot.section === "chorus" ? 1.58 : 1.24,
        y: snapshot.section === "chorus" ? -42 : -20,
        filter: "blur(12px)",
        duration: snapshot.section === "chorus" ? 0.9 : 0.72,
        ease: "power2.out",
        onComplete: () => echo.remove(),
      },
    );
  }

  #fontScaleFor(phrase) {
    const count = phrase.charCount || phrase.text.length;
    if (count > 64) return "0.62";
    if (count > 48) return "0.7";
    if (count > 36) return "0.82";
    if (count > 26) return "0.92";
    return "1";
  }

  #visibleWordIndexes(snapshot) {
    const visible = new Set();
    const activeIndex = this.#activeWordIndex(snapshot);
    if (activeIndex < 0) return visible;

    const activeWord = this.wordSequence[activeIndex]?.unit;
    const englishFocus = this.#isEnglishWord(activeWord);
    const chorusFocus = snapshot.section === "chorus";
    const maxChars = englishFocus ? (chorusFocus ? 15 : 18) : chorusFocus ? 10 : 16;
    const maxWords = englishFocus ? 2 : chorusFocus ? 2 : 4;
    let totalChars = 0;

    const addWord = (index, { allowExpired = false, requireSameLanguage = false } = {}) => {
      const word = this.wordSequence[index]?.unit;
      if (!word || visible.has(index) || visible.size >= maxWords) return false;
      if (!allowExpired && (word.endTime ?? word.startTime ?? 0) < snapshot.position - 24) return false;
      if (requireSameLanguage && this.#isEnglishWord(word) !== englishFocus) return false;
      const length = this.#wordLength(word);
      const nextTotal = totalChars + length + (visible.size > 0 ? 1 : 0);
      if (visible.size > 0 && nextTotal > maxChars) return false;
      visible.add(index);
      totalChars = nextTotal;
      return true;
    };

    addWord(activeIndex);
    let before = activeIndex - 1;
    let after = activeIndex + 1;

    if (englishFocus || chorusFocus) {
      while (visible.size < maxWords && after < this.wordSequence.length) {
        addWord(after, { requireSameLanguage: englishFocus });
        after += 1;
      }

      return visible;
    }

    while (visible.size < maxWords && (before >= 0 || after < this.wordSequence.length)) {
      const addedAfter = after < this.wordSequence.length ? addWord(after, { allowExpired: true }) : false;
      if (after < this.wordSequence.length) after += 1;

      if (visible.size >= maxWords) break;

      const addedBefore = before >= 0 ? addWord(before, { allowExpired: true }) : false;
      if (before >= 0) before -= 1;

      if (!addedAfter && !addedBefore) break;
    }

    return visible;
  }

  #activeWordIndex(snapshot) {
    if (!this.wordSequence.length) return -1;

    const snapshotWordIndex = this.wordSequence.findIndex(({ unit }) => unit === snapshot.word);
    if (snapshotWordIndex >= 0) return snapshotWordIndex;

    const containingIndex = this.wordSequence.findIndex(({ unit }) => unit.contains?.(snapshot.position));
    if (containingIndex >= 0) return containingIndex;

    const upcomingIndex = this.wordSequence.findIndex(({ unit }) => {
      const end = unit.endTime ?? unit.startTime ?? 0;
      return end >= snapshot.position - 80;
    });

    return upcomingIndex >= 0 ? upcomingIndex : this.wordSequence.length - 1;
  }

  #wordLength(word) {
    return Math.max(1, Array.from(word.text.trim()).length);
  }

  #isEnglishWord(word) {
    return Boolean(word?.text && ENGLISH_WORD_PATTERN.test(word.text));
  }

  #phraseHasEnglish(phrase) {
    return Boolean(phrase?.children?.some((word) => this.#isEnglishWord(word)));
  }

  #holdWeightForChar(char) {
    if (!char?.text || /\s/.test(char.text)) return 0;

    const start = char.startTime || 0;
    const end = Math.max(char.endTime || start, start);
    const duration = end - start;
    const durationWeight = invLerp(190, 760, duration);
    const vowelWeight = VOWELISH_CHAR_PATTERN.test(char.text) ? 1 : 0.68;
    return clamp(durationWeight * vowelWeight);
  }

  #archiveExcerpt(text) {
    const characters = Array.from(text.trim());
    if (characters.length <= 18) return text.trim();
    return `${characters.slice(0, 18).join("")}...`;
  }

  #isEmphasisWord(word) {
    if (!word) return false;
    if (EMPHASIS_POS.has(word.pos)) return true;
    return invLerp(0, 9, word.text.trim().length) > 0.48;
  }

  #phraseEmotion(snapshot) {
    const vocal = snapshot.metrics?.vocal || 0;
    const arousal = Math.max(0, snapshot.metrics?.arousal || 0);
    return clamp(vocal * 0.58 + snapshot.chorusIntensity * 0.34 + arousal * 0.18);
  }

  #phraseBreath(phrase) {
    const duration = Math.max(320, (phrase.endTime || 0) - (phrase.startTime || 0));
    return clamp(invLerp(700, 3600, duration), 0.25, 1);
  }

  #clearPhrase() {
    if (!this.activePhrase || this.isClearing) return;
    this.isClearing = true;
    this.currentElement.classList.remove("has-phrase");
    gsap.to(this.currentElement, {
      opacity: 0,
      y: -12,
      filter: "blur(8px)",
      "--phrase-presence": 0,
      duration: 0.42,
      ease: "power2.inOut",
      onComplete: () => {
        this.activePhrase = null;
        this.previousPhraseText = "";
        this.charViews = [];
        this.wordViews.clear();
        this.wordSequence = [];
        this.visibleWords.clear();
        this.currentElement.replaceChildren();
        this.isClearing = false;
      },
    });
  }

  #clearFloatingText() {
    gsap.killTweensOf(Array.from(this.archiveElement.children));
    gsap.killTweensOf(Array.from(this.echoElement.children));
    this.archiveElement.replaceChildren();
    this.echoElement.replaceChildren();
  }
}
